import { fg } from '@atlassian/jira-feature-gating';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type { UseCheckAssetsUser } from './WorkspaceContext.types.tsx';

const PERMISSIONS_BULK_PERMITTED_URL = '/gateway/api/permissions/bulk/permitted';
const ERROR_META = {
	packageName: 'jiraServicedeskInsightCommonWorkspaceContext',
	teamName: 'astral',
};

export type PermissionsResponse = PermissionsRequestPayload & {
	dontRequirePrincipalInSite: boolean;
	permitted: boolean;
};

export type PermissionsRequestPayload = {
	principalId: string;
	resourceId: string;
	permissionId: string;
};

export type PermissionsTransformerResponse = {
	isValidAssetsUser: boolean;
	isAssetsAdmin: boolean;
};

/**
 * Generates the permissions request payload for checking user permissions
 */
export const getPermissionsRequestPayload = (
	userId: string,
	cloudId: string,
): PermissionsRequestPayload[] => {
	if (!userId || !cloudId) {
		fireErrorAnalytics({
			error: new Error('User ID or cloud ID is missing in getPermissionsRequestPayload'),
			meta: {
				...ERROR_META,
				id: 'getPermissionsRequestPayload',
			},
		});
		return [];
	}
	return [
		{
			resourceId: `ari:cloud:cmdb::site/${cloudId}`,
			principalId: `ari:cloud:identity::user/${userId}`,
			permissionId: 'write',
		},
		{
			resourceId: `ari:cloud:cmdb::site/${cloudId}`,
			principalId: `ari:cloud:identity::user/${userId}`,
			permissionId: 'manage',
		},
	];
};

/**
 * Extracts permission information from the API response
 */
export const extractPermissionsResponse = (
	response: PermissionsResponse[],
): PermissionsTransformerResponse => {
	return {
		isValidAssetsUser: response.some(
			(permission) => permission.permissionId === 'write' && permission.permitted,
		),
		isAssetsAdmin: response.some(
			(permission) => permission.permissionId === 'manage' && permission.permitted,
		),
	};
};

/**
 * Validates tenant context has required fields for permission checks
 */
export const validateTenantContext = (
	tenantContext: TenantContext,
): { isValid: boolean; error?: Error } => {
	if (!tenantContext) {
		return {
			isValid: false,
			error: new Error('Tenant context is missing'),
		};
	}

	if (!tenantContext.atlassianAccountId || !tenantContext.cloudId) {
		return {
			isValid: false,
			error: new Error('Tenant context is missing required fields (atlassianAccountId or cloudId)'),
		};
	}

	return { isValid: true };
};

/**
 * Creates a standardized error response for permission checks
 */
export const createDefaultResponse = (error: Error | null): UseCheckAssetsUser => {
	return {
		isValidAssetsUser: null,
		isAssetsAdmin: false,
		error: error ?? new Error('Error while fetching user permissions'),
		isLoading: false,
		isUnitsEnabledForAssets: false,
	};
};

/**
 * Creates a standardized success response for permission checks
 */
export const createSuccessResponse = (
	isValidAssetsUser: boolean,
	isAssetsAdmin: boolean,
): UseCheckAssetsUser => {
	return {
		isValidAssetsUser,
		isAssetsAdmin,
		error: null,
		isLoading: false,
		isUnitsEnabledForAssets: false,
	};
};

/**
 * Fetches user permissions. `cmdbSiteCloudId` overrides `tenantContext.cloudId`
 * in the payload when the astral_units_workspace_host_resolver gate is ON.
 * @throws Error if the API call fails
 */
export const fetchUserPermissions = async (
	tenantContext: TenantContext,
	cmdbSiteCloudId?: string | null,
): Promise<PermissionsTransformerResponse> => {
	const resolvedCloudId =
		cmdbSiteCloudId && fg('astral_units_workspace_host_resolver')
			? cmdbSiteCloudId
			: (tenantContext.cloudId ?? '');

	const payload = getPermissionsRequestPayload(
		tenantContext.atlassianAccountId ?? '',
		resolvedCloudId,
	);

	if (payload.length === 0) {
		throw new Error('Failed to create permissions request payload');
	}

	const response = await performPostRequest<PermissionsResponse[]>(PERMISSIONS_BULK_PERMITTED_URL, {
		body: JSON.stringify(payload),
	});

	if (!Array.isArray(response)) {
		throw new Error('Invalid response format from permissions API');
	}

	return extractPermissionsResponse(response);
};
